import { TopicGroup } from './topicalContentData';

interface ProductTopicSourceConfig {
  path: string;
  prefix: string;
  leadingGroupName?: string;
}

const TOPIC_HEADER_PREFIX = '##';
const GROUP_DIVIDER = '---';
const TOPIC_LINE_REGEX = /^\d+\.\s+(.*)$/;

/**
 * Why this matters: Ron's source files are hand-curated grouped topic lists, not
 * typed TS datasets. This parser converts them into the same shape the topics
 * agent UI already expects so we can preserve the exact arrangement without
 * manually re-entering hundreds of titles.
 */
export const parseProductTopicGroups = (raw: string, prefix: string, leadingGroupName?: string): TopicGroup[] => {
  const groups: TopicGroup[] = [];
  let currentGroup: TopicGroup | null = null;
  let pendingLeadingTopics = false;

  const startGroup = (name: string) => {
    const groupId = `${prefix}-group-${groups.length + 1}`;
    currentGroup = {
      id: groupId,
      name,
      color: PRODUCT_GROUP_COLORS[groups.length % PRODUCT_GROUP_COLORS.length],
      topics: [],
    };
    groups.push(currentGroup);
  };

  for (const rawLine of raw.split(/\r?\n/)) {
    const line = rawLine.trim().replace(/^"+|"+$/g, '');

    if (!line || line === GROUP_DIVIDER) {
      continue;
    }

    if (line.startsWith(TOPIC_HEADER_PREFIX)) {
      startGroup(cleanGroupName(line));
      continue;
    }

    const topicMatch = line.match(TOPIC_LINE_REGEX);
    if (!topicMatch) {
      continue;
    }

    if (!currentGroup) {
      startGroup(leadingGroupName || 'Foundations');
      pendingLeadingTopics = true;
    }

    const title = topicMatch[1].trim().replace(/^"+|"+$/g, '');
    const activeGroup: TopicGroup | null = currentGroup ?? groups[groups.length - 1] ?? null;
    if (!activeGroup) {
      continue;
    }

    activeGroup.topics.push({
      id: `${prefix}-topic-${groups.length}-${activeGroup.topics.length + 1}`,
      title,
      // Why this matters: these source docs only provide article titles, so the
      // title is also the safest initial keyword seed until separate keyword data exists.
      primaryKeyword: title,
      secondaryKeywords: [],
    });
  }

  const parsedGroups = groups.filter((group) => group.topics.length > 0);

  if (pendingLeadingTopics && parsedGroups.length > 0 && leadingGroupName) {
    parsedGroups[0] = { ...parsedGroups[0], name: leadingGroupName };
  }

  return parsedGroups;
};

const PRODUCT_GROUP_COLORS = [
  '#F59E0B',
  '#3B82F6',
  '#10B981',
  '#EF4444',
  '#8B5CF6',
  '#D97706',
  '#EC4899',
  '#6366F1',
  '#14B8A6',
];

const cleanGroupName = (rawGroupName: string): string =>
  rawGroupName
    .replace(/^##\s*/, '')
    .replace(/\*\*/g, '')
    .replace(/^[^\w(]+/, '')
    .trim();

/**
 * Why this matters: product topic pages can read directly from the founder-provided
 * source files in `public/keywords`, keeping the content workflow aligned with the
 * latest raw inputs instead of a manually duplicated dataset.
 */
export const loadProductTopicGroups = async ({
  path,
  prefix,
  leadingGroupName,
}: ProductTopicSourceConfig): Promise<TopicGroup[]> => {
  const response = await fetch(path);

  if (!response.ok) {
    throw new Error(`Failed to load product topics from ${path}`);
  }

  const raw = await response.text();
  return parseProductTopicGroups(raw, prefix, leadingGroupName);
};
